/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Accordion } from '@squiz/sds';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPONENT_SERVICE_STRINGS } from '../../../../../../../../Constants/Strings.constants';
import { findComponentOverrides } from '../../../../utils/FindComponentOverrides/FindComponentOverrides';

import { ComponentOverride } from './OverridesAccordion.defs';

import { OverridesAccordionProps } from './OverridesAccordion.props';

/**
 * The component environment variable override accordion in the component set environment variable dialog.
 * @param {OverridesAccordionProps} props Props passed to <OverridesAccordion />.
 * @export
 * @returns {<JSX.Element />} The `<OverridesAccordion />` component.
 */
export const OverridesAccordion = (props: OverridesAccordionProps): JSX.Element => {
  const { t } = useTranslation();

  const [componentOverrides] = useState<Array<ComponentOverride>>(findComponentOverrides(props));

  const labels = useMemo(() => {
    return {
      componentSpecificOverrides: t(COMPONENT_SERVICE_STRINGS.componentSpecificOverrides),
      noComponentSpecificOverrides: t(COMPONENT_SERVICE_STRINGS.noComponentSpecificOverrides),
    };
  }, [t]);

  const accordionHeader = (override: ComponentOverride): JSX.Element => {
    return (
      <span className="env-var-dialog__accordion-header">
        {override.componentName + ' - ' + override.componentVersion}
      </span>
    );
  };

  const accordionBody = (override: ComponentOverride): Array<JSX.Element> => {
    const body: Array<JSX.Element> = [];
    for (const record of override.variables) {
      for (const [varName, value] of Object.entries(record)) {
        body.push(
          <div className="env-var-dialog__accordion-body" key={varName}>
            <span className="env-var-dialog__accordion-body-name">{varName}</span>
            <span>{value}</span>
          </div>,
        );
      }
    }
    return body;
  };

  const accordionContainerWithoutOverrides = (): JSX.Element => {
    return (
      <div className="env-var-dialog__accordion-body--empty-container">
        <span>{labels.noComponentSpecificOverrides}</span>
      </div>
    );
  };

  const accordionContainer = (): Array<JSX.Element> => {
    return componentOverrides.map((entry) => (
      <div className="env-var-dialog__accordion-body--detailed-state" key={entry.componentName}>
        <Accordion body={accordionBody(entry)} header={accordionHeader(entry)} />
      </div>
    ));
  };

  return (
    <div className="env-var-dialog__accordion-container">
      {/* Accordions to display specific overrides */}
      <div>
        <span className="env-var-dialog__accordion-title">{labels.componentSpecificOverrides}</span>
      </div>
      {/* If no overrides show no overrides text */}
      {componentOverrides.length === 0 ? accordionContainerWithoutOverrides() : accordionContainer()}
    </div>
  );
};
